import { defineStore, acceptHMRUpdate } from 'pinia';
import { url } from '@/conf/conf.js';
import LocalStorage from '@/controller/LocalStorage.js';
import { useVehiclesStore } from '@/stores/vehicle.js';
import { storeToRefs } from "pinia";
import FormController from "@/controller/FormController.vue";
import { middleware } from "@/services/api/Middleware.js";
const isNorthgate = LocalStorage.isNorthgate();

export const useAppointmentStore = defineStore({
    id: "appointment",
    state: () => ({
        advisors: [],
        form: {
            dealer: {},
            branch_id: '',
            advisor_id: '',
            start_date: '',
            vehicle_id: '',
            firstname: '',
            lastname: '',
            second_lastname: '',
            vat_number: '',
            mobile: '',
            email: '',
            categories: [],
            selectedCategories: [],
            category: '',
            actions: [],
            comments: '',
            vouchers: [],
            invalidVouchers: [],
            client_type: 0,
            type: {},
            client: undefined,
            advisor: undefined,
        },
        selectedDealer: undefined,
        selectedDate: undefined,
        selectedSlot: undefined,
        dealerSlots: [],
        dealerActionsLoading: false,        
        activeStep: 1,
        validSteps: {
            all: false,
            workshop: false,
            advisor: true,
            reason: true,
            slot: false,
            contact: false,
        },
        lockedSteps: {
            workshop: false,
            advisor: false,
            reason: true,
            slot: true,
            contact: true,
            advisor: true,
        },
        creatingAppointment: false,
        appointmentDone: false,
        errors: [],
        fieldErrors: {},
        formError: null,
        dealerHasSlots: false,
        userKey: LocalStorage.getKey(),
        disabledMonthDays: [],
        maxDaysToCheck: -1,
        selectedDealerChangedFromSearch: false
    }),
    getters: {
        getSelectedDate(state){
            const dateTime = state.selectedSlot?.start_date || state.selectedDate;
            if (dateTime && typeof dateTime === 'string' && dateTime.includes(':')) {
                return dateTime.replace(/(\d{2}:\d{2}):\d{2}/, '$1');
            }
            return dateTime;
        },
        getSelectedDealerChangedFromSearch(state) {
            return state.selectedDealerChangedFromSearch;
        },
        getDisabledMonthDays(state){
            return state.disabledMonthDays;
        },
        getMaxDays(state){
            return state.maxDaysToCheck;
        },
        getSelectedBranch(state){
            return state.form.dealer;
        },
        getFormData(state) {
            return state.form;
        },      
        getSelectedDealer(state) {
            return state.form.dealer;
        },
        getActiveStep(state) {
            return state.activeStep;
        },
        getSelectedCategoriesDescriptions(state) {
            let descriptions = undefined;
            if(this.form.selectedCategories.length){
                descriptions = this.form.selectedCategories.map(item => item.description);
            }
            return descriptions ? descriptions.join(', ') : '';
        },
        getSelectedSlot(state){
            return state.selectedSlot;
        },
        getDealerSlots(state) {
            return state.dealerSlots;
        },
        getDealerActionsLoading(state) {
            return state.dealerActionsLoading;
        },
        getFullName(state) {
            return `${state.form.firstname} ${state.form.lastname} ${state.form.second_lastname}`;
        },
        getValidSteps(state) {
            return state.validSteps;
        },  
        getLockedSteps(state) {
            return state.lockedSteps;
        },
        isCreatingAppointment(state){
            return state.creatingAppointment;
        },
        getErrors(state){
            return state.errors;
        },
        getDealerAdvisorPermission(state){
            return state.form.dealer.advisor_selection;
        },
        hasSlots(state){
            return state.dealerHasSlots;
        },
        isFormValid(state){
            return state.validSteps.all;
        },
        getSelectedAdvisor(state){
            return state.form.advisor;
        },
        getSelectedAdvisorName(state){
            const advisorId = state.form.advisor_id || state.form.advisor;
            return state.advisors.find(advisor => advisor.id === advisorId)?.fullname || '';
        },
        getContactErrorFields(state){
            return {
                firstname: !this.validateField('firstname', state.form.firstname) || state.fieldErrors.firstname,
                lastname: !this.validateField('lastname', state.form.lastname) || state.fieldErrors.lastname,
                second_lastname: !this.validateField('second_lastname', state.form.second_lastname) || state.fieldErrors.second_lastname,
                vat_number: !this.validateField('vat_number', state.form.vat_number) || state.fieldErrors.vat_number,
                mobile: !this.validateField('mobile', state.form.mobile) || state.fieldErrors.mobile,
                email: !this.validateField('email', state.form.email) || state.fieldErrors.email,
            }
        },
        getFieldErrors(state){
            return state.fieldErrors;
        },
        getFormError(state){
            return state.formError;
        }       
    },
    actions: {
        resetForm() {
            this.form.dealer.selected = false;
            this.form =  {
                dealer: {},
                branch_id: '',
                advisor_id: '',
                start_date: '',
                vehicle_id: '',
                firstname: '',
                lastname: '',
                second_lastname: '',
                vat_number: '',
                mobile: '',
                email: '',
                categories: [],
                selectedCategories: [],
                category: '',
                actions: [],
                comments: '',
                vouchers: [],
                invalidVouchers: [],
                client_type: 0,
                type: {},
                client: {}
            };
            this.selectedDealer = undefined;
            this.selectedDate = undefined;
            this.selectedSlot = undefined;
            this.dealerSlots = [];
            this.dealerActionsLoading = false;        
            this.activeStep = 1;
            this.validSteps = {
                all: false,
                workshop: false,
                advisor: false,
                reason: true,
                slot: false,
                contact: false,
            };
            this.lockedSteps = {
                workshop: false,
                advisor: true,
                reason: true,
                slot: true,
                contact: true,
            };
            this.dealerHasSlots = false;
        },
        changeSelectedDealer(params, fromSearch) {
            this.form.dealer.selected = false;
            this.form.dealer = params;
            this.validSteps.workshop = true;
            this.dealerSlots = [];
            this.dealerHasSlots = params.has_slots == 1;
            this.selectedDealerChangedFromSearch = fromSearch;
        },
        changeSelectedAdvisor(params) {
            if(!params){
                this.form.advisor = undefined;
                this.form.advisor_id = undefined;
                return;
            }
            this.form.advisor = params;
            this.form.advisor_id = params;
            this.validSteps.advisor = true;
        },
        changeSelectedCategory(params) {
            if(this.form.category){
                this.form.category.selected = false;
            }
            this.form.category = params;
        },
        addAction(params, categoryId){
            this.form.actions.push({
                description: params.description,
                action_base_id: params.action_base_id,
                importer_id: params.importer_id,
                action_base_category_id: categoryId
            })
        },
        removeAction(params){
            params.selected = false;
            this.form.actions = this.form.actions.filter(item => item.action_base_id !== params.action_base_id);
            
            // Verificar si la categoría de esta acción ya no tiene acciones seleccionadas
            const categoryId = params.action_base_category_id;
            const hasActionsInCategory = this.form.actions.some(action => action.action_base_category_id === categoryId);
            if (!hasActionsInCategory) {
                this.removeCategory(categoryId);
            }
        },
        removeCategory(categoryId){
            this.form.selectedCategories = this.form.selectedCategories.filter(item => item.id !== categoryId);
        },
        addCategory(category){
            const hasObjectWithIdOne = this.form.selectedCategories.some(item => item.id === category.id);
            if(!hasObjectWithIdOne){
                this.form.selectedCategories.push(category);
            }
        },
        actionSelectionChange(params, category){
            if(params.selected){
                this.addAction(params, category.id);
                this.addCategory(category);
            }else{
                this.removeAction(params);
            }
        },
        setStep(params){
            this.activeStep = params;
        },
        async nextStep(params){
            const skipAdvisorAndSlots = LocalStorage.isNorthgate();
            
            if(skipAdvisorAndSlots){
                if(this.activeStep === 1){
                    this.activeStep = 3;
                    this.unlockStep(this.activeStep);
                    this.validateStep('all');
                    return;
                }
                if(this.activeStep === 3){
                    this.activeStep = 5;
                    this.unlockStep(this.activeStep);
                    this.validateStep('all');
                    return;
                }
                if(this.activeStep === 5){
                    if(this.validSteps.workshop && this.validSteps.reason && this.validSteps.contact){
                        this.unlockStep(this.activeStep);
                        const result = await this.createAppointment();
                        if(result && result.success){
                            this.activeStep = 6;
                        }
                    }
                    this.validateStep('all');
                    return;
                }
                return;
            }
            if(this.activeStep === 5){
                if(this.dealerHasSlots){
                    if(this.validSteps.workshop && 
                        this.validSteps.contact &&
                        this.validSteps.slot){
                            this.unlockStep(this.activeStep);
                            const result = await this.createAppointment();
                            if(result && result.success){
                                this.activeStep++;
                            }
                    }  
                }else if( this.validSteps.workshop && 
                    this.validSteps.contact){
                        this.unlockStep(this.activeStep);
                        const result = await this.createAppointment();
                        if(result && result.success){
                            this.activeStep++;
                        }
                    }
            }else if(this.activeStep == 3 && !this.dealerHasSlots){
                this.activeStep = 5;
                this.unlockStep(this.activeStep);
            }else if(this.activeStep == 1 && this.form.dealer.advisor_selection == 0 && !params){
                this.activeStep = 3;
            }
            else{
                this.activeStep++;
                this.unlockStep(this.activeStep);
            }
            this.validateStep('all');
            console.log('active step: ', this.activeStep);
        },
        previousStep(){
            const skipAdvisorAndSlots = LocalStorage.isNorthgate();
            
            if(skipAdvisorAndSlots){
                if(this.activeStep == 6){
                    this.activeStep = 5;
                    return;
                }
                if(this.activeStep == 5){
                    this.activeStep = 3;
                    return;
                }
                if(this.activeStep == 4){
                    this.activeStep = 3;
                    return;
                }
                if(this.activeStep == 3){
                    this.activeStep = 1;
                    return;
                }
                return;
            }
            if(this.activeStep == 5 && !this.dealerHasSlots){
                this.activeStep = 3;
            }else if (this.activeStep == 3 && this.form.dealer.advisor_selection == 0){
                this.activeStep = 1;
            }
            else if(this.activeStep > 1){
                this.activeStep--;
            }
        },
        unlockStep(step){
            switch (step) {
                case 1:
                    this.lockedSteps.workshop = false;
                break;
                case 2:
                    this.lockedSteps.reason = false;
                break;
                case 3:
                    this.lockedSteps.slot = false;
                break;
                case 4:
                    this.lockedSteps.contact = false;
                break;
                default:
                break;
            }
        },
        validateStep(step){
            let valid = false;
            switch (step) {
                case 'all':
                    valid = this.validSteps.workshop && this.validSteps.reason && this.validSteps.contact;
                    if(this.dealerHasSlots){
                        valid = valid && this.validSteps.slot;
                    }    
                    this.validSteps.all = valid;
                break;
                case 1:
                    valid = this.form.dealer && this.form.dealer.dealer_id;
                    this.validSteps.workshop = valid;
                break;
                case 2:
                    valid = this.form.actions.length > 0;    
                    this.validSteps.reason = valid;    
                break;
                case 3:  
                    valid = this.form.start_date != '' && this.form.start_date != undefined;  
                    this.validSteps.slot = valid;  
                break;
                case 4:
                    let contactErrors = this.getContactErrorFields;   
                    valid = !contactErrors.firstname &&
                            !contactErrors.vat_number && 
                            !contactErrors.mobile && 
                            !contactErrors.email;
                    if(this.form.client_type == 0){
                        valid = valid && !contactErrors.lastname && !contactErrors.second_lastname;
                    }
                    this.validSteps.contact = valid;
                    this.validateStep('all');
                break;
                default:
                    valid = false;
                break;
            }
            return valid;
        },
        setDate(params){
            const date = new Date(params);
            const startOfDayUtc = new Date(date);
            const endOfDayUtc = new Date(date);
            startOfDayUtc.setDate(startOfDayUtc.getDate() + 1);
            endOfDayUtc.setDate(endOfDayUtc.getDate() + 1);


            startOfDayUtc.setUTCHours(0, 0, 0);
            endOfDayUtc.setUTCHours(23, 59, 59);
            const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
            this.selectedDate = date.toLocaleDateString('es-ES', options);
            if(this.selectedSlot) {
                this.selectedSlot.selected = false;
                this.selectedSlot = undefined;
            }
            this.form.start_date = '';
            this.validSteps.slot = false;
            return this.fetchDealerSlots({
                k: this.userKey,
                dealer_id: this.form.dealer.dealer_id,
                branch_id: this.form.dealer.branch.id,
                advisor_id: this.form.advisor,
                start_date: Math.floor(startOfDayUtc.getTime() / 1000),
                end_date: Math.floor(endOfDayUtc.getTime() / 1000),
            })
        },
        setTime(slot){
            console.log('Selected slot:', slot);
            if(this.selectedSlot) this.selectedSlot.selected = false;
            this.selectedSlot = slot;
            console.log('selected slot start', this.selectedSlot.start_date);
            
            this.form.start_date = slot.start_date;
            this.validSteps.slot = true;
        },
        setComments(params){
            this.form.comments = params;
        },
        setPersonalInfo(field,value){
            if(field != 'client'){
                this.form[field] = value;
                // Si hi ha un error en aquest camp, el netegem
                if(this.fieldErrors[field]){
                    delete this.fieldErrors[field];
                }
            }else{
                this.form.firstname = value.firstname;
                this.form.lastname = value.lastname;
                this.form.second_lastname = value.second_lastname;
                this.form.email = value.email;
                this.form.mobile = value.mobile;
                this.form.vat_number = value.vat_number;
                this.form.client_type = value.client_type;
                this.form.client = value;
                this.fieldErrors = {};
            }
            this.validSteps.contact = this.validateStep(4);
        },
        async fetchDealerActions(params) {
            let lang = params.lang;
            this.dealerActionsLoading = true;
            try {
                let options = {
                    method: "POST",
                    headers: {
                        'Accept': "application/json",
                        'Content-Type': "application/json",
                    },
                    body: JSON.stringify({
                        validation_key: params.k,
                        dealer_id: params.dealer_id,
                        lang: params.lang.toUpperCase(),
                        method: "getDealerActions",
                    }),
                };
                return middleware(url, options, ['isTokenAlive'])()
                .then((response) => response.json())
                .then((data) => {
                    if(data && data.categories){
                        if(data.categories.length > 0){
                            data.categories[0].selected = true;
                            this.changeSelectedCategory(data.categories[0]);
                        }
                        this.dealerActionsLoading = false;
                        this.form.categories = data.categories;
                        return data.categories;
                    }else{
                        this.dealerActionsLoading = false;
                        return [];
                    }
                })
                .catch((error) => {
                    this.dealerActionsLoading = false;
                });
            } catch (error) {
                this.dealerActionsLoading = false;
            }
        },
        async fetchAdvisors(params) {
            try {
                let options = {
                    method: "POST",
                    headers: {
                        'Accept': "application/json",
                        'Content-Type': "application/json",
                    },
                    body: JSON.stringify({
                        validation_key: params.k,
                        dealer_id: params.dealer_id,
                        branch_id: params.branch_id,
                        method: "getAdvisors",
                    }),
                };
                return middleware(url, options, ['isTokenAlive'])()
                .then((response) => response.json())
                .then((data) => {
                    if(data && data.advisors){
                        console.log('Advisors fetched:', data.advisors);
                        this.advisors = data.advisors;
                        return data;
                    }else{
                        return [];
                    }
                })
                .catch((error) => {
                });
            } catch (error) {
            }
        },
        async fetchDealerSlots(params) {
            try {
                let options = {
                    method: "POST",
                    headers: {
                        'Accept': "application/json",
                        'Content-Type': "application/json",
                    },
                    body: JSON.stringify({
                        validation_key: params.k,
                        dealer_id: params.dealer_id,
                        branch_id: params.branch_id,
                        advisor_id: params.advisor_id,
                        start_date: params.start_date,//1703458800
                        end_date: params.end_date,//1703545199
                        method: "getDealerSlots",
                    }),
                };
                return middleware(url, options, ['isTokenAlive'])()
                .then((response) => response.json())
                .then((data) => {
                    if(data && data.slots){
                        this.dealerSlots = data.slots;
                        return data.slots;
                    }else{
                        return [];
                    }
                })
                .catch((error) => {
                });
            } catch (error) {
            }
        },
        async fetchDisabledMonthDays(params) {
            try {
                let options = {
                    method: "POST",
                    headers: {
                        'Accept': "application/json",
                        'Content-Type': "application/json",
                    },
                    body: JSON.stringify({
                        validation_key: params.k,
                        dealer_id: params.dealer_id,
                        branch_id: params.branch_id,
                        start_date: params.start_date,
                        advisor_id: this.form.advisor,
                        end_date: params.end_date,
                        method: "getMonthDisabledDays",
                    }),
                };
                return middleware(url, options, ['isTokenAlive'])()
                .then((response) => response.json())
                .then((data) => {
                    
                    if(data && data.non_working_days){
                        
                        this.disabledMonthDays = data.non_working_days;
                    }
                    if(data && data.max_days){
                        this.maxDaysToCheck = data.max_days;
                        
                        return data.max_days;
                    }else{
                        this.maxDaysToCheck=0;
                        return [];
                    }
                })
                .catch((error) => {
                });
            } catch (error) {
            }
        },
        async createAppointment(){
            const vehicleStore = useVehiclesStore();
            const vehicle = storeToRefs(vehicleStore);
            const { getVehiclesSelected: vehicleSelected } = storeToRefs(vehicle);
            this.formError = null;
            this.fieldErrors = {};
            this.errors = [];
            this.creatingAppointment = true;
            
            let formData = {
                dealer_id: this.form.dealer.dealer_id,
                branch_id: this.form.dealer.branch.id,
                vehicle_id: vehicleSelected && vehicleSelected != '' ? vehicleSelected.value.id : '',
                firstname:this.form.firstname,
                lastname:this.form.lastname,
                second_lastname:this.form.second_lastname,
                vat_number:this.form.vat_number,
                mobile:this.form.mobile,
                email:this.form.email,
                client_type:this.form.client_type,
                comments:this.form.comments,
                discount_codes: this.form.vouchers,
                actions: this.form.actions,
            };
            if (this.form.advisor && this.form.advisor > 0){
                formData.advisor_id = this.form.advisor;
            }
            if(this.dealerHasSlots){
                let parts = this.form.start_date.split(/[/ :]/);
                let date = new Date(Date.UTC(parts[2], parts[1] - 1, parts[0], parts[3], parts[4], parts[5]));
                let timestamp = date.getTime() / 1000;
                formData.start_date = timestamp;
            }
            try {
                let options = {
                    method: "POST",
                    headers: {
                        'Accept': "application/json",
                        'Content-Type': "application/json",
                    },
                    body: JSON.stringify(Object.assign({}, {
                        validation_key: this.userKey,
                        method: "createAppointment",
                    }, formData)),
                };
                return middleware(url, options, ['isTokenAlive'])()
                .then((response) => response.json())
                .then((data) => {
                    if(!data || data === null){
                        this.creatingAppointment = true;
                        this.appointmentDone = true;
                        this.formError = null;
                        this.fieldErrors = {};
                        return { success: true };
                    }
                    if(data && data.errors && data.errors.length > 0){
                        this.errors.push(`Si us plau revisi les dades introduïdes. Error al camp: ${data.errors[0].field}`);
                        this.appointmentDone = false;
                        this.creatingAppointment = false;
                        this.fieldErrors = {};
                        this.fieldErrors[data.errors[0].field] = true;
                        this.formError = data.errors[0].error;
                        return { success: false, error: data.errors[0].error, field: data.errors[0].field };
                    }
                    else{
                        this.creatingAppointment = true;
                        this.appointmentDone = true;
                        this.formError = null;
                        this.fieldErrors = {};
                        return { success: true };
                    }
                })
                .catch((error) => {
                    this.creatingAppointment = false;
                });
            } catch (error) {
                this.creatingAppointment = false;
            }
        },    
        async validateVoucher(params) {
            this.form.invalidVouchers = [];
            try {
                let options = {
                    method: "POST",
                    headers: {
                        'Accept': "application/json",
                        'Content-Type': "application/json",
                    },
                    body: JSON.stringify({
                        validation_key: params.k,
                        dealer_id: this.form.dealer.dealer_id,
                        code: params.code,
                        method: "validateVoucher",
                    }),
                };
                return middleware(url, options, ['isTokenAlive'])()
                .then((response) => response.json())
                .then((data) => {
                    if(data && data.codes && data.codes.length > 0){
                        this.form.vouchers.push(data.codes[0]);
                        return data;
                    }else{
                        this.form.invalidVouchers.push(params.code);
                        return [];
                    }
                })
                .catch((error) => {
                });
            } catch (error) {
            }
        },
        validateField(field, value){
            let valid = false;
            let min = false;
            let max = false;
            let validarNifCif = false;
            let validEmail = false;
            switch (field) {
                case 'firstname':
                    min = FormController.minRule(value, 1);
                    max = FormController.maxRule(value, 80);
                    if(isNorthgate){
                        valid = min && max;
                    }else{
                        valid = max;
                    }
                break;
                case 'lastname':
                    min = FormController.minRule(value, 1);
                    max = FormController.maxRule(value, 50);
                    valid = min && max;
                break;
                case 'second_lastname':
                    min = FormController.minRule(value, 1);
                    max = FormController.maxRule(value, 50);
                    if(isNorthgate){
                        valid = min && max;
                    }else{
                        valid = max;
                    }
                break;
                case 'vat_number':
                    min = FormController.minRule(value, 1);
                    max = FormController.maxRule(value, 20);
                    validarNifCif = FormController.validarNifCif(value);
                    if(isNorthgate){
                        valid = min && max && validarNifCif;
                    }else if(min){
                        valid = min && max && validarNifCif;
                    }else{
                        valid  = true;
                    }
                break;
                case 'mobile':
                    min = FormController.minRule(value, 9);
                    max = FormController.maxRule(value, 30);
                    valid = min && max;
                break;
                case 'email':
                    min = FormController.minRule(value, 1);
                    max = FormController.maxRule(value, 100);
                    validEmail = FormController.validEmail(value);
                    valid = min && max && validEmail;
                break;
                default:
                break;
            }
            return valid;
        },
        updateUserKey() {
            this.userKey = LocalStorage.getKey();
        }  

    }
});


if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAppointmentStore, import.meta.hot))
}